import { FieldGroupTypes } from '@/features/RfpForm/types/FieldGroupTypes'
import type { RfpFieldGroup } from '@/features/RfpForm/types/RfpFields'
import type { RfpFormData } from '@/features/RfpForm/types/RfpFormData'
import type { RfpFormStep } from '@/features/RfpForm/types/RfpFormSteps'
import type { GalaxyRfpConfig, GalaxyRfpFormStep, RfpHotel } from '@/AppRfp/types/GalaxyRfpFormItem'
import {
  createAcceptTermsField,
  createEventNameField,
  createLocationField,
} from './rfp/createFields'

/**
 * Transform data from JSON to be compatible
 * with the DbSelect component options
 *
 * For these dropdown, the label and the value is exactly the same
 */
function useTransformRawDataToOptions(unitItems: { name: string }[]) {
  return [
    ...unitItems.map((item) => {
      return {
        label: item.name,
        value: item.name,
      }
    }),
  ]
}

/**
 * Some fields have a different format than what is expected
 * so we transform this data
 */
export const transformGalaxyStepToRfpStep = (
  galaxyStep: GalaxyRfpFormStep,
  configuration: GalaxyRfpConfig,
): RfpFormStep => {
  return {
    id: galaxyStep.id,
    name: galaxyStep.name,
    rfp_fields: galaxyStep.rfp_fields.map((field) => {
      switch (field.field_id) {
        case FieldGroupTypes.EVENT_TYPE:
          return {
            ...field,
            event_types: useTransformRawDataToOptions(field.event_types),
          }
        case FieldGroupTypes.ROOM_SETUP:
          return {
            ...field,
            function_room_types: field.function_room_types.map((item) => {
              return {
                ...item,
                id: String(item.id), // temporary fix, as for now we need a string here for typescript
                default_icon:
                  configuration.all_function_room_types.find((el) => el.id === item.id)
                    ?.default_icon || '',
              }
            }),
          }
        case FieldGroupTypes.BUDGET:
          return {
            ...field,
            currencies: useTransformRawDataToOptions(field.currencies),
          }
        case FieldGroupTypes.ADDITIONAL_INFORMATION_DROPDOWN:
          return {
            ...field,
            dropdown_options: useTransformRawDataToOptions(field.dropdown_options),
          }
        case FieldGroupTypes.ADDITIONAL_INFORMATION:
          return {
            ...field,
            field_type: field.field_type_id === 2 ? 'textarea' : 'input',
          }
        default:
          return field
      }
    }),
  }
}

/**
 * Parse query parameters and detect RFP locations
 *
 * Only the locations coming from the JSON of locations
 * will be returned, if the user set an non valid location
 * it will not be added
 */
const getLocationsFromUrl = (locationList: RfpHotel[]) => {
  const locations: string[] = []
  const urlParams = new URLSearchParams(window.location.search)
  urlParams
    ?.get('locations')
    ?.split(',')
    ?.map((loc) => {
      const findEl = locationList.find((hotel) => String(hotel.db2_rfp_location_id) === loc)
      if (findEl) {
        locations.push(findEl.name)
      } else {
        console.error(`RFP - Query param location=${loc} is not in the list of available locations`)
      }
    })

  return locations
}

export const getFormDefaultData = (configuration: GalaxyRfpConfig): RfpFormData => {
  const defaultData: RfpFormData = {
    selectedLocations: [],
    eventName: '',
  }

  // Add locations field if needed (only for group RFP forms)
  if (configuration.hotels?.length) {
    defaultData.selectedLocations = getLocationsFromUrl(configuration.hotels)
  } else if (configuration.form.hotel?.db2_rfp_location_id) {
    defaultData.selectedLocations = [configuration.form.hotel.db2_rfp_location_id.toString()]
  }

  // For now we only have 1 default value so it is fine like that
  // later we might want to make this more generic
  if (configuration.form.modelData?.currency) {
    defaultData.currency = configuration.form.modelData?.currency
  }

  return defaultData
}

/**
 * Takes Galaxy json configuration and transform it
 * in data for the RFP components.
 *
 * It also sets the default data for the form
 */
export const getComponentData = (configuration: GalaxyRfpConfig): RfpFormStep[] => {
  const steps = configuration.form.steps.map((step) =>
    transformGalaxyStepToRfpStep(step, configuration),
  )
  const lastStepIndex = steps.length - 1

  /**
   * These fields are not part of the steps sent by Galaxy,
   * we put them at the beginning ourselves
   */
  const fieldsAtTheBeginning: RfpFieldGroup[] = []

  /**
   * These fields are not part of the steps sent by Galaxy,
   * we put them at the end ourselves
   */
  const fieldsAtTheEnd: RfpFieldGroup[] = []

  // Add locations field if needed (only for group RFP forms)
  if (configuration.hotels?.length) {
    const fieldLocations = createLocationField(configuration.hotels)
    fieldsAtTheBeginning.push(fieldLocations)
  }

  // Add "Event name" field
  fieldsAtTheBeginning.push(createEventNameField())

  // Add checkbox terms and conditions
  fieldsAtTheEnd.push(createAcceptTermsField(configuration.form.terms))

  steps[0].rfp_fields.unshift(...fieldsAtTheBeginning)
  steps[lastStepIndex].rfp_fields.push(...fieldsAtTheEnd)

  return steps
}
